import React, { useEffect, useState } from 'react';
import { Table, Button, Toast, Spin, Tag, Select, Modal } from '@douyinfe/semi-ui';
import { API } from '../helpers';
import { matchPath, navigate } from '../router';

const RANGE_OPTIONS = [
  { value: 'today', label: '今日' },
  { value: '7d',    label: '近 7 天' },
  { value: '30d',   label: '近 30 天' },
  { value: 'all',   label: '全部' },
];

const STATUS_OPTIONS = [
  { value: '',  label: '全部状态' },
  { value: '1', label: '成功' },
  { value: '0', label: '失败' },
];

function maskKey(k) {
  if (!k) return '';
  return k.slice(0, 6) + '...' + k.slice(-6);
}

// 把任意值兜底转成可读字符串
// 历史上 prompt_text 可能被存成对象/数组/字符串混在一起 — 渲染前统一处理,避免出现 [object Object]
function safeText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const j = JSON.stringify(v);
    return j.length > 200 ? j.slice(0, 200) + '…' : j;
  } catch {
    return '';
  }
}

function fmtQuota(n) {
  return Number(n || 0).toLocaleString();
}

function fmtTime(ms) {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString('zh-CN', { hour12: false });
}

const AdminTokenDetail = () => {
  const path = window.location.pathname;
  const params = matchPath('/admin/tokens/:id', path) || {};
  const tokenId = +params.id;

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState({ current: 1, pageSize: 20, total: 0 });
  const [range, setRange] = useState('7d');
  const [statusFilter, setStatusFilter] = useState('');
  const [promptModal, setPromptModal] = useState(null); // null 或 { id, seq, model, prompt_text, created_at }

  // 打开"用户提问"详情模态框
  const openPromptModal = (record) => {
    setPromptModal({
      id: record.id,           // 保留 logs.id,供运维查 DB 定位用
      seq: record.seq,         // token 内调用序号,展示给管理员看
      model: record.model,
      created_at: record.created_at,
      prompt_text: safeText(record.prompt_text),
    });
  };

  const loadInfo = () => {
    setLoading(true);
    API.get(`/api/admin/tokens/${tokenId}`)
      .then((r) => setInfo(r.data.data))
      .catch((e) => Toast.error(e.response?.data?.message || '加载失败'))
      .finally(() => setLoading(false));
  };

  const loadLogs = (p = page.current, s = page.pageSize) => {
    setLogsLoading(true);
    const params = new URLSearchParams({ range, page: String(p), page_size: String(s) });
    if (statusFilter) params.set('status', statusFilter);
    API.get(`/api/admin/tokens/${tokenId}/logs?${params.toString()}`)
      .then((r) => {
        const d = r.data.data || {};
        setLogs(d.items || []);
        setPage((prev) => ({ ...prev, current: d.page || 1, pageSize: d.page_size || s, total: d.total || 0 }));
      })
      .catch((e) => Toast.error(e.response?.data?.message || '加载日志失败'))
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => {
    if (!tokenId) return;
    loadInfo();
  }, [tokenId]);

  useEffect(() => {
    if (!tokenId) return;
    loadLogs(1, page.pageSize);
  }, [range, statusFilter, tokenId]);

  // 从「在飞请求」面板点令牌进来时带 focus=logs,直接滚到调用日志区,
  // 免得管理员还要自己往下滑一屏才看到记录。
  useEffect(() => {
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (focus !== 'logs') return;
    const el = document.getElementById('token-logs');
    if (!el) return;
    const timer = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    return () => clearTimeout(timer);
  }, [tokenId]);

  if (loading) return <Spin spinning />;
  if (!info) return <div style={{ padding: 20, color: '#86909c' }}>Token 不存在或加载失败</div>;

  const t = info.token || {};
  const sum = info.summary || {};
  const win = info.windows || {};

  const columns = [
    // token 内调用序号(按 created_at 升序连续编号,与全局 logs.id 解耦)
    { title: '序号', dataIndex: 'seq', width: 60, render: (s) => <span style={{ color: '#1d7dfa', fontWeight: 500 }}>#{s}</span> },
    { title: '时间', dataIndex: 'created_at', width: 150, render: fmtTime },
    { title: '模型', dataIndex: 'model', width: 140 },
    {
      title: '用户提问', dataIndex: 'prompt_text', width: 320,
      render: (raw, record) => {
        const p = safeText(raw);
        if (!p) return <span style={{ color: '#c9cdd4' }}>—</span>;
        return (
          <span
            onClick={() => openPromptModal(record)}
            style={{
              color: '#18181b', fontSize: 12, cursor: 'pointer',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
              lineHeight: 1.5, maxHeight: '3em',
            }}
            title="点击查看完整内容"
          >
            {p}
          </span>
        );
      },
    },
    { title: '扣除额度', dataIndex: 'quota', width: 100, render: (q) => <span style={{ color: '#f56565' }}>{fmtQuota(q)}</span> },
    { title: '耗时', dataIndex: 'duration_ms', width: 90, render: (d) => d ? (d > 1000 ? (d / 1000).toFixed(2) + 's' : d + 'ms') : '—' },
    {
      title: '状态', dataIndex: 'status', width: 140,
      render: (s, r) => (
        <span>
          <Tag color={s === 1 ? 'green' : 'red'} size="small">{s === 1 ? '成功' : (s === 2 ? '拦截' : '失败')}</Tag>
          <span style={{ marginLeft: 6, color: '#86909c', fontSize: 11 }}>HTTP {r.http_status}</span>
        </span>
      ),
    },
    { title: '渠道', dataIndex: 'channel_id', width: 70, render: (c) => c ? '#' + c : '—' },
    { title: '流式', dataIndex: 'is_stream', width: 60, render: (s) => s ? '是' : '否' },
    {
      title: '错误', dataIndex: 'error', width: 240,
      render: (e) => e ? <span style={{ color: '#f56565', fontSize: 12, wordBreak: 'break-all' }}>{e}</span> : '—',
    },
  ];

  return (
    <div>
      {/* 顶部:标题在左,Key+复制+返回按钮在右 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
            令牌详情 #{t.id} · {t.name}
          </h2>
          <Tag color={t.status === 1 ? 'green' : 'red'} size="small">{t.status_text}</Tag>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button
            size="small" type="primary" theme="light"
            onClick={() => navigate('/admin/tokens')}
          >← 返回列表</Button>
        </div>
      </div>

      {/* 核心额度 + 调用 2 大卡 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12 }}>剩余额度</div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: t.unlimited ? '#1d7dfa' : '#18181b' }}>
            {t.unlimited ? '不限' : fmtQuota(t.remain_quota) + ' 额度'}
          </div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4 }}>
            {t.unlimited ? '永久有效' : `总额度 ${fmtQuota(t.remain_quota + t.used_quota)} · 已使用 ${fmtQuota(t.used_quota)}`}
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12 }}>累计调用</div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6 }}>{fmtQuota(sum.total_calls)} 次</div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4 }}>
            成功 <span style={{ color: '#00b42a' }}>{fmtQuota(sum.success_calls)}</span> / 失败 <span style={{ color: '#f56565' }}>{fmtQuota(sum.failed_calls)}</span>
            <span style={{ marginLeft: 8, color: '#c9cdd4' }}>·</span>
            今日 <span style={{ color: '#18181b' }}>{fmtQuota(win.today?.calls || 0)}</span> 次
          </div>
        </div>
      </div>

      {/* 最后调用时间(单卡,系统按次计费,Tokens 不再展示) */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e6eb', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ color: '#86909c', fontSize: 12 }}>最后调用时间</span>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{sum.last_call_at ? fmtTime(sum.last_call_at) : '从未调用'}</span>
      </div>

      {/* 详细日志表 */}
      <div id="token-logs" style={{ background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8 }}>
          <strong>调用日志</strong>
          <Select
            style={{ width: 120 }}
            optionList={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
          />
          <Select
            style={{ width: 110 }}
            optionList={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <span style={{ color: '#86909c', fontSize: 12 }}>共 {page.total} 条</span>
        </div>
        <Spin spinning={logsLoading}>
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={logs}
            pagination={{
              currentPage: page.current,
              pageSize: page.pageSize,
              total: page.total,
              showSizeChanger: true,
              pageSizeOpts: [10, 20, 50, 100],
              onPageSizeChange: (s) => loadLogs(1, s),
              onPageChange: (p) => loadLogs(p, page.pageSize),
            }}
          />
        </Spin>
      </div>

      {/* 用户提问详情模态框 */}
      <Modal
        title={promptModal ? `用户提问 #${promptModal.seq ?? promptModal.id}` : '用户提问'}
        visible={!!promptModal}
        onCancel={() => setPromptModal(null)}
        onOk={() => setPromptModal(null)}
        okText="关闭"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={720}
        style={{ top: 40 }}
      >
        {promptModal && (
          <div>
            <div style={{ fontSize: 12, color: '#86909c', marginBottom: 12 }}>
              模型:<Tag size="small" color="blue" style={{ marginLeft: 4 }}>{promptModal.model}</Tag>
              时间:<span style={{ marginLeft: 4 }}>
                {new Date(promptModal.created_at).toLocaleString('zh-CN', { hour12: false })}
              </span>
              字符数:<span style={{ marginLeft: 4, fontWeight: 500 }}>{promptModal.prompt_text.length}</span>
            </div>
            <div
              style={{
                background: '#fafbfc',
                border: '1px solid #e5e6eb',
                borderRadius: 6,
                padding: 12,
                fontSize: 13,
                color: '#1d2129',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 480,
                overflowY: 'auto',
              }}
            >
              {promptModal.prompt_text || <span style={{ color: '#c9cdd4' }}>(无内容)</span>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminTokenDetail;